import { cva } from "class-variance-authority";

/**
 * Unified status badge for payments, registrations, sessions, awards, etc.
 * Variants map to our state colour system.
 */
const badge = cva(
  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] whitespace-nowrap",
  {
    variants: {
      tone: {
        success: "bg-emerald-100 text-emerald-700 border border-emerald-200",
        warning: "bg-[#D4AF37]/15 text-[#0A192F] border border-[#D4AF37]/40",
        danger: "bg-red-100 text-red-700 border border-red-200",
        info: "bg-[#1E3A8A]/10 text-[#1E3A8A] border border-[#1E3A8A]/20",
        neutral: "bg-slate-100 text-slate-600 border border-slate-200",
        navy: "bg-[#0A192F] text-[#D4AF37] border border-[#D4AF37]/30",
        gold: "bg-[#D4AF37] text-[#0A192F] border border-[#B5952F]",
      },
    },
    defaultVariants: { tone: "neutral" },
  }
);

const PAYMENT_MAP = {
  paid: "success",
  succeeded: "success",
  pending: "warning",
  processing: "warning",
  failed: "danger",
  refunded: "neutral",
};

const REG_MAP = {
  registered: "info",
  allocated: "navy",
  waitlisted: "warning",
  confirmed: "success",
  cancelled: "danger",
  withdrawn: "neutral",
};

const SESSION_MAP = {
  scheduled: "neutral",
  in_progress: "success",
  completed: "info",
  paused: "warning",
};

const AWARD_MAP = {
  "Best Delegate": "gold",
  "High Commendation": "navy",
  "Outstanding Diplomacy": "info",
  "Honorable Mention": "warning",
  "Verbal Mention": "neutral",
  "Participation": "neutral",
};

export default function StatusBadge({ status, type = "payment", children, className = "", ...rest }) {
  const label = children ?? status;
  const map = {
    payment: PAYMENT_MAP,
    registration: REG_MAP,
    session: SESSION_MAP,
    award: AWARD_MAP,
  }[type] || {};
  const tone = map[status] || "neutral";
  return (
    <span className={`${badge({ tone })} ${className}`} data-testid={rest["data-testid"] || `badge-${type}-${status}`}>
      {label}
    </span>
  );
}

export function PaymentStatusBadge(props) { return <StatusBadge type="payment" {...props} />; }
export function RegistrationStatusBadge(props) { return <StatusBadge type="registration" {...props} />; }
export function SessionStatusBadge(props) { return <StatusBadge type="session" {...props} />; }
export function AwardBadge(props) { return <StatusBadge type="award" {...props} />; }
